/**
 * InvestingArena API client
 *
 * Fetches AI trade ideas and portfolio data from ai-portfolio-arena.onrender.com.
 * Activity/trades are proxied through the Bloom backend for translation support.
 */

import axios from 'axios';
import { backendUrl } from '../constants';
import i18n from '../i18n';

import claudeIcon from '../assets/Icons/claude.svg';
import geminiIcon from '../assets/Icons/gemini.svg';
import openaiIcon from '../assets/Icons/openai.svg';

const INVESTING_ARENA_BASE_URL = 'https://ai-portfolio-arena.onrender.com';

// Types
export type AIModelId = 'openai' | 'claude' | 'gemini';

export interface InvestingArenaTrade {
  date: string;
  symbol: string;
  action: 'BUY' | 'SELL';
  shares: number;
  fill_price: number;
  order_id: string;
  rationale: string;
  conviction: string | null;
  catalysts: string[];
  risks: string[];
  portfolio_percentage: number;
  portfolio_id: AIModelId;
  relative_time: string;
}

export interface InvestingArenaPortfolio {
  id: AIModelId;
  name: string;
  color: string;
  icon: string;
  paper: boolean;
  account_value: number;
  cash: number;
  return_pct: number;
  pnl: number;
  total_deposits: number;
}

export interface InvestingArenaPosition {
  symbol: string;
  shares: number;
  avg_cost: number;
  current_price: number;
  market_value: number;
  unrealized_pnl: number;
  unrealized_pnl_pct: number;
  // API actually returns these field names (from Alpaca)
  unrealized_pl?: number;
  unrealized_plpc?: number;
}

export interface ChartDataPoint {
  time: number; // Unix timestamp in seconds
  value: number;
}

// Chart data response keyed by AI model ID
export interface ChartDataResponse {
  claude: ChartDataPoint[];
  openai: ChartDataPoint[];
  gemini: ChartDataPoint[];
}

// AI Model display names
export const AI_MODEL_NAMES: Record<AIModelId, string> = {
  openai: 'GPT 5.2',
  claude: 'Opus 4.6',
  gemini: 'Gemini 3',
};

// AI Model colors
export const AI_MODEL_COLORS: Record<AIModelId, string> = {
  openai: '#000000',
  claude: '#D97757',
  gemini: '#3186FF',
};

// AI Model icon paths (SVG assets)
export const AI_MODEL_ICONS: Record<AIModelId, string> = {
  claude: claudeIcon,
  openai: openaiIcon,
  gemini: geminiIcon,
};

/** Returns non-English language code for arena API translation, or undefined. */
const getArenaLanguage = (): string | undefined => {
  const lang = i18n.language?.split('-')[0];
  return lang && lang !== 'en' ? lang : undefined;
};

/**
 * Get all recent trades across all AI portfolios.
 * Routes through Bloom backend for translation support,
 * falls back to direct external API on failure.
 */
export const getInvestingArenaActivity = async (
  portfolioId?: AIModelId,
  limit?: number,
  symbol?: string,
): Promise<InvestingArenaTrade[]> => {
  // Try Bloom backend proxy (supports translation)
  try {
    const params: Record<string, string> = {};
    if (portfolioId) params.model = portfolioId;
    if (limit) params.limit = limit.toString();
    if (symbol) params.symbol = symbol;
    const lang = getArenaLanguage();
    if (lang) params.language = lang;

    const response = await axios.get<InvestingArenaTrade[]>(`${backendUrl}/api/arena/activity/`, {
      params,
      timeout: 30000,
    });
    return response.data;
  } catch (backendError) {
    console.warn('[InvestingArena] Backend proxy failed, falling back to direct API:', backendError);
  }

  // Fallback: direct external API call (no translation)
  try {
    const params = new URLSearchParams();
    if (portfolioId) params.append('model', portfolioId);
    if (limit) params.append('limit', limit.toString());
    if (symbol) params.append('symbol', symbol);

    const queryString = params.toString();
    const url = `${INVESTING_ARENA_BASE_URL}/api/activity`;
    const fullUrl = queryString ? `${url}?${queryString}` : url;

    const response = await axios.get<InvestingArenaTrade[]>(fullUrl);
    return response.data;
  } catch (error) {
    console.error('[InvestingArena] Failed to fetch activity:', error);
    return [];
  }
};

/**
 * Get all AI portfolio summaries
 */
export const getInvestingArenaPortfolios = async (): Promise<InvestingArenaPortfolio[]> => {
  try {
    const response = await axios.get<InvestingArenaPortfolio[]>(`${INVESTING_ARENA_BASE_URL}/api/portfolios`);
    return response.data;
  } catch (error) {
    console.error('[InvestingArena] Failed to fetch portfolios:', error);
    throw error;
  }
};

/**
 * Get a single portfolio by ID
 */
export const getInvestingArenaPortfolio = async (portfolioId: AIModelId): Promise<InvestingArenaPortfolio | null> => {
  try {
    const response = await axios.get<InvestingArenaPortfolio>(
      `${INVESTING_ARENA_BASE_URL}/api/portfolio/${portfolioId}`,
    );
    return response.data;
  } catch (error) {
    console.error(`[InvestingArena] Failed to fetch portfolio ${portfolioId}:`, error);
    return null;
  }
};

/**
 * Get current positions for a portfolio
 */
export const getInvestingArenaPositions = async (portfolioId: AIModelId): Promise<InvestingArenaPosition[]> => {
  try {
    const response = await axios.get<InvestingArenaPosition[]>(
      `${INVESTING_ARENA_BASE_URL}/api/portfolio/${portfolioId}/positions`,
    );
    return response.data;
  } catch (error) {
    console.error(`[InvestingArena] Failed to fetch positions for ${portfolioId}:`, error);
    return [];
  }
};

/**
 * Get current positions for a portfolio — throws on error so React Query can set isError.
 * Use this variant when callers need to distinguish between "empty portfolio" and "fetch failed".
 */
export const getInvestingArenaPositionsOrThrow = async (portfolioId: AIModelId): Promise<InvestingArenaPosition[]> => {
  const response = await axios.get<InvestingArenaPosition[]>(
    `${INVESTING_ARENA_BASE_URL}/api/portfolio/${portfolioId}/positions`,
  );
  return response.data;
};

/**
 * Get cumulative returns chart data keyed by AI model
 */
export const getInvestingArenaChartData = async (
  period: '1W' | '1M' | '3M' | 'YTD' | 'ALL' = '1M',
): Promise<ChartDataResponse> => {
  try {
    const response = await axios.get<ChartDataResponse>(`${INVESTING_ARENA_BASE_URL}/api/chart-data?period=${period}`);
    return response.data;
  } catch (error) {
    console.error('[InvestingArena] Failed to fetch chart data:', error);
    return { claude: [], openai: [], gemini: [] };
  }
};
